import { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff, Gem } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('customer');
  const [companyName, setCompanyName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
        toast.success('Login successful!');
        navigate('/tasks');
      } else {
        const result = await register(email, password, name, role, companyName || null);
        if (result.pending) {
          toast.success('Registration successful! Please wait for admin approval.');
          setIsLogin(true);
        } else {
          toast.success('Registration successful!');
          navigate('/tasks');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image with Navy Overlay */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1639065643006-e217c4fee12e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxkaWFtb25kJTIwYnJhY2VsZXQlMjBqZXdlbHJ5JTIwZWxlZ2FudCUyMGRhcmslMjBsdXh1cnl8ZW58MHx8fHwxNzcxMDU5NzcyfDA&ixlib=rb-4.1.0&q=85)' }}
      >
        <div className="absolute inset-0" style={{ backgroundColor: 'hsla(220, 60%, 20%, 0.75)' }} />
        <div className="relative z-10 p-12 flex flex-col justify-between h-full">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <img 
              src="/ks-logo.png" 
              alt="K's Jewellery"
              className="w-14 h-14 object-contain"
            />
            <div>
              <h2 className="text-2xl font-bold" style={{ color: 'hsl(45, 90%, 50%)' }}>K's</h2>
              <p className="text-white/60 text-xs uppercase tracking-wider">Jewellery</p>
            </div>
          </div>
          
          {/* Bottom Text */}
          <div>
            <h1 className="text-4xl font-bold text-white mb-4">
              Product Development <br />
              <span style={{ color: 'hsl(45, 90%, 50%)' }}>Tracker</span>
            </h1>
            <p className="text-lg text-white/80">
              Precision CAD Management for Jewelry Design Teams
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <img 
              src="/ks-logo.png" 
              alt="K's Jewellery"
              className="w-12 h-12 object-contain"
            />
            <div>
              <h2 className="text-xl font-bold" style={{ color: 'hsl(220, 60%, 20%)' }}>K's Jewellery</h2>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-border p-8">
            <h2 className="text-2xl font-semibold text-foreground mb-2">
              {isLogin ? 'Welcome Back' : 'Create Account'}
            </h2>
            <p className="text-muted-foreground mb-8 text-sm">
              {isLogin ? 'Sign in to your account' : 'Register for a new account'}
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <div>
                  <Label htmlFor="name" className="text-foreground font-medium text-sm">Full Name</Label>
                  <Input
                    id="name"
                    data-testid="register-name-input"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required={!isLogin}
                    placeholder="John Doe"
                    className="mt-1.5 h-10"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="email" className="text-foreground font-medium text-sm">Email or Username</Label>
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="email or username"
                  className="mt-1.5 h-10"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-foreground font-medium text-sm">Password</Label>
                <div className="relative mt-1.5">
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    className="h-10 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {!isLogin && (
                <div>
                  <Label htmlFor="role" className="text-foreground font-medium text-sm">Role</Label>
                  <select
                    id="role"
                    data-testid="register-role-select"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className="mt-1.5 w-full h-10 px-3 rounded-md border border-input bg-white text-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                  >
                    <option value="customer">Customer</option>
                    <option value="executor">Executor (Internal Team)</option>
                    <option value="special_executor">Special Executor (Team Lead)</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              )}

              {!isLogin && (
                <div>
                  <Label htmlFor="company" className="text-foreground font-medium text-sm">Company Name (Optional)</Label>
                  <Input
                    id="company"
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Your Company"
                    className="mt-1.5 h-10"
                  />
                </div>
              )}

              <Button
                type="submit"
                data-testid="login-submit-button"
                disabled={loading}
                className="w-full h-10 text-sm font-medium"
                style={{ backgroundColor: 'hsl(220, 60%, 20%)', color: 'white' }}
              >
                {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
              </Button>
            </form>

            <div className="mt-6 text-center space-y-2">
              {isLogin && (
                <Link
                  to="/forgot-password"
                  className="text-muted-foreground hover:text-foreground text-sm"
                >
                  Forgot password?
                </Link>
              )}
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="block w-full text-sm font-medium"
                style={{ color: 'hsl(45, 90%, 45%)' }}
              >
                {isLogin ? "Don't have an account? Register" : 'Already have an account? Sign In'}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
